import React, { Component } from 'react';
import { inject } from 'mobx-react';

import { BorderedTab, BorderedTabs, Button, Dialog, Flex, Spinner, Suspense, Text } from 'core/components';
import { FormDialog } from 'core/form';
import FormGroupIndicator from 'core/form/components/FormGroupIndicator';
import storeLoader from 'app/stores/storeLoader';
import { options, getFields } from 'app/forms/config/deviceDetails';
import GeneralSettings from 'app/views/settings/devices/form/GeneralSettings';
// import PlanSite from 'app/views/settings/devices/form/PlanSite';
import BGPSettings from 'app/views/settings/devices/form/BGPSettings';
import IpSnmpSettings from 'app/views/settings/devices/form/IpSnmpSettings';
import Integrations from 'app/views/settings/devices/form/Integrations';
import StSettings from 'app/views/settings/devices/form/StSettings';
import { getAssociatedUserRbacLabels } from 'app/stores/auth/AuthRbac';
import DeviceClassIcon from 'app/components/nms/DeviceClassIcon';
import CommandAccessSettings from 'app/views/settings/devices/form/CommandAccessSettings';
import FlowSettings from './form/FlowSettings';

const DEVICE_FORM_MIN_WIDTH = 1024;

@storeLoader('$sites', '$mitigations.platformsCollection', '$metrics.monitoringTemplatesCollection')
@inject('$devices', '$dictionary', '$setup', '$credentials', '$kentikAgents', '$auth')
export default class DeviceFormDialog extends Component {
  state = {
    loadingBgp: false,
    loadingDevice: true
  };

  componentDidMount() {
    const { $devices, $setup, $credentials, $kentikAgents, $auth, model } = this.props;

    if (model && !model.isNew) {
      model.fetch().then(() => this.setState({ loadingDevice: false }));
    } else {
      model.set('labels', getAssociatedUserRbacLabels('device'));
      this.setState({ loadingDevice: false });
    }

    $devices.fetchMasterBGPDevices();
    $setup.getPlans();
    $credentials.groupedCollection.fetch();
    if ($auth.hasPermission('recon.enabled', { overrideForSudo: false })) {
      $kentikAgents.collection.fetch({ force: true });
    }

    this.getBgpIngestIps();
  }

  onSubmit = async (form, values) => {
    const { onClose, onSubmit, model } = this.props;
    let snmp_enabled = false;
    const bgp_enabled = values.device_bgp_type !== 'none';

    const device_snmp_v3_conf_enabled = !!values.device_snmp_v3_conf;
    const isKproxyCollectionEnabled =
      values.snmp_collection_method?.includes('kproxy') || values.snmp_collection_method?.includes('direct_to_saas');

    if (device_snmp_v3_conf_enabled) {
      snmp_enabled = 'V3';
    }

    // Set snmp_enabled if device_snmp_ip is provided OR if kproxy/direct_to_saas collection is enabled
    // Only set to V2 if V3 is not already configured
    if (!device_snmp_v3_conf_enabled && (values.device_snmp_ip || isKproxyCollectionEnabled)) {
      snmp_enabled = 'V2';
    }

    if (bgp_enabled) {
      values.use_asn_from_flow = false;
    }

    if (Array.isArray(values.snmp_collection_method)) {
      // See https://kentik.atlassian.net/browse/NMS-323 for details; note that this comes in as an array
      switch (values.snmp_collection_method.toSorted().join(',')) {
        case 'kentik_agent':
        case 'kentik_agent_flow_only':
          values.use_ranger_interface_metrics = 'true';
          break;
        default:
          values.use_ranger_interface_metrics = 'false';
      }
    }

    await model.save({ ...values, snmp_enabled, bgp_enabled });

    if (typeof onSubmit === 'function') {
      await onSubmit(values);
    }

    if (typeof onClose === 'function') {
      await onClose();
    }
  };

  getBgpIngestIps = (field, value) => {
    const { $devices, model } = this.props;
    const isBgpIngestSet = !!model.get('bgpPeerIP4');
    const bgpType = value || model.get('device_bgp_type');
    const deviceId = model.get('id');

    if (!isBgpIngestSet && bgpType === 'device') {
      this.setState({ loadingBgp: true });

      $devices.fetchBgpIngestIps(deviceId).then((result) => {
        if (result) {
          model.set('bgpPeerIP4', result.bgpPeerIP4);
          model.set('bgpPeerIP6', result.bgpPeerIP6);
        }

        this.setState({ loadingBgp: false });
      });
    }
  };

  renderTabTitle = (form, tab) => (
    <Flex alignItems="center">
      <FormGroupIndicator form={form} fields={tab.fields} icon={tab.icon} mr="6px" />
      <Text>{tab.title}</Text>
    </Flex>
  );

  render() {
    const { $dictionary, $metrics, $auth, onClose, model, ...rest } = this.props;
    const { loadingBgp, loadingDevice } = this.state;

    const isDeviceconfigFeatureFlagEnabled = $auth.hasPermission('recon.deviceConfigFetch.enabled', {
      overrideForSudo: false
    });

    const formFields = getFields();

    const tabs = [
      {
        id: 'general',
        title: 'General',
        fields: ['device_name', 'device_description', 'device_type', 'device_flow_type', 'site.id'],
        panel: <GeneralSettings role="tabpanel" />
      },
      {
        id: 'flow',
        title: 'Flow',
        fields: ['device_flow_type', 'sending_ips', 'device_sample_rate', 'plan.id'],
        panel: <FlowSettings role="tabpanel" />
      },
      {
        id: 'ipSnmp',
        title: 'SNMP',
        fields: [
          'snmp_collection_method',
          'device_snmp_ip',
          'kentik_agent_snmp_options.credential_name',
          'kentik_agent_snmp_options.agent_id',
          'kentik_agent_snmp_options.port',
          'kentik_agent_snmp_options.ranger_snmp_ip',
          'device_snmp_v3_conf_enabled',
          'minimize_snmp',
          'device_snmp_v3_conf.UserName',
          'device_snmp_v3_conf.AuthenticationProtocol',
          'device_snmp_v3_conf.AuthenticationPassphrase',
          'device_snmp_v3_conf.PrivacyProtocol',
          'device_snmp_v3_conf.PrivacyPassphrase',
          'monitoring_template_id'
        ],
        panel: <IpSnmpSettings role="tabpanel" />
      },
      {
        id: 'st',
        title: 'Streaming Telemetry',
        fields: ['streaming_telemetry_collection_method'],
        panel: <StSettings />
      },
      {
        id: 'bgp',
        title: 'BGP',
        fields: [
          'device_bgp_type',
          'device_bgp_neighbor_ip',
          'device_bgp_neighbor_ip6',
          'device_bgp_neighbor_asn',
          'device_bgp_password',
          'device_bgp_flowspec',
          'device_bgp_label_unicast',
          'bgp_lookup_strategy',
          'use_bgp_device_id'
        ],
        panel: <BGPSettings onChangeBgpType={this.getBgpIngestIps} loadingBgp={loadingBgp} role="tabpanel" />
      },
      {
        id: 'integrations',
        title: 'Integrations',
        fields: ['device_alert'],
        panel: <Integrations role="tabpanel" />
      }
    ];

    if (isDeviceconfigFeatureFlagEnabled) {
      // Include Config Sync tab if the feature flag is enabled
      tabs.push({
        id: 'cmdaccess',
        title: 'SSH',
        fields: [
          'deviceconf_options.enabled',
          'deviceconf_options.agent_id',
          'deviceconf_options.hostname',
          'deviceconf_options.auth_method',
          'deviceconf_options.credential_name',
          'deviceconf_options.platform',
          'deviceconf_options.fetch_enabled',
          'deviceconf_options.fetch_interval',
          'deviceconf_options.fetch_timeout',
          'deviceconf_options.command_enabled'
        ],
        panel: <CommandAccessSettings role="tabpanel" />
      });
    }

    return (
      <Suspense
        loading={loadingDevice}
        fallback={
          <Dialog isOpen width={820} maxHeight="calc(100vh - 40px)">
            <Dialog.Body p={3} flexDirection="column" display="flex">
              <Spinner />
            </Dialog.Body>
          </Dialog>
        }
      >
        {!loadingDevice && (
          <FormDialog
            {...rest}
            model={model}
            fields={formFields}
            options={options}
            title={
              model.isNew ? (
                'Device'
              ) : (
                <Flex alignItems="center" gap="4px">
                  Device: <DeviceClassIcon showTooltip deviceModel={model} style={{ margin: 0 }} />
                  {model.get('device_name')}
                </Flex>
              )
            }
            entityName="Device"
            isOpen
            onClose={onClose}
            formActionsProps={{ onCancel: onClose, onSubmit: this.onSubmit, submitButtonText: 'Submit' }}
            minWidth={DEVICE_FORM_MIN_WIDTH}
            alignSelf="flex-start"
            maxHeight="calc(100vh - 40px)"
            dialogBodyProps={{ overflow: 'hidden', p: 0, display: 'flex', flexDirection: 'column' }}
            formComponentProps={{ large: true }}
          >
            {({ form }) => (
              <>
                {!model.isNew && model.isArchived && (
                  <Flex flexDirection="column" alignItems="center" justifyContent="center" p={3}>
                    <Text style={{ fontSize: 16, marginBottom: 8 }}>
                      {model.get('device_name')} has been <strong>archived</strong>.
                    </Text>
                    <Button intent="primary" onClick={() => this.onSubmit(form, { device_status: 'V' })}>
                      Unarchive now
                    </Button>
                  </Flex>
                )}
                {(model.isNew || model.isActive || model.isIncomplete) && (
                  <BorderedTabs
                    selectedTabBackgroundColor="dialogBackground"
                    pt={2}
                    overflow="hidden"
                    width="auto"
                    tabIndent={16}
                    minimal
                  >
                    {tabs.map((tab) => (
                      <BorderedTab key={tab.id} id={tab.id} title={this.renderTabTitle(form, tab)} panel={tab.panel} />
                    ))}
                  </BorderedTabs>
                )}
              </>
            )}
          </FormDialog>
        )}
      </Suspense>
    );
  }
}
